import React from "react";
import { ErrorState } from "app/components/ui/feedback";

/**
 * Catches an error thrown while a part of the screen is drawn, so one broken
 * table / page shows a small "couldn't be shown — Try again" message instead of
 * the whole app going blank (production) or the red error overlay (development).
 *
 *   <ErrorBoundary compact name="Documents table">…</ErrorBoundary>   a section
 *   <ErrorBoundary resetKey={location.pathname}>…</ErrorBoundary>     a whole page
 *
 * resetKey: when it changes (e.g. the user opens another page) the error is cleared.
 * The error is still written to the browser console for diagnosis.
 */
export default class ErrorBoundary extends React.Component {
  state = { error: null };

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // keep the real error visible to developers
    console.error(`[ErrorBoundary${this.props.name ? `: ${this.props.name}` : ""}]`, error, info?.componentStack);
  }

  componentDidUpdate(prevProps) {
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) this.setState({ error: null });
  }

  retry = () => this.setState({ error: null });

  render() {
    if (!this.state.error) return this.props.children;
    const { compact = false } = this.props;
    return (
      <ErrorState
        compact={compact}
        title={compact ? "This section couldn't be shown" : "This page couldn't be shown"}
        message="Something went wrong while drawing it. Press Retry — if it happens again, reload the page."
        onRetry={this.retry}
      />
    );
  }
}
